//== state → DOM rendering for derived and state-pass-through display.
//== two element types, both keyed off data-format for the formatter:
//==   [data-derive="key"]      reads from calculate(state).key
//==   [data-show="path.to.x"]  reads from state via dot-notation path
//== inputs.js owns input field formatting; this module owns read-only output.

import {
  calculate,
  PRODUCTS,
  originalEquitySharePct,
  isCurrentEquityLocked,
  resolveCurrentEquityPct,
  resolveInputs,
} from './calculate.js'
import { formatMoney, formatMoneyCompact, formatPercent, formatDate } from '../calc-shared/format.js'
import {
  validateStep0,
  validateStep1,
  validateStep2,
  validateStep3,
} from './validate.js'
import { isArmed, subscribeArmed } from './armed.js'

const STEP_VALIDATORS = [validateStep0, validateStep1, validateStep2, validateStep3]

const PLACEHOLDER = '-'

//== formatters expect raw numeric input (or ISO date string for "date").
//== "percent" assumes percentage form (10 -> "10.00%") and is what the
//== state stores. "percent-decimal" assumes decimal form (0.10 -> "10.00%")
//== and is what calculate() returns - convert at the render boundary.
const FORMATTERS = {
  percent: (value) => formatPercent(value),
  'percent-decimal': (value) => formatPercent(value * 100),
  money: formatMoney,
  'money-compact': formatMoneyCompact,
  date: formatDate,
  plain: (value) => String(value),
}

const PRODUCT_LABELS = {
  newBuild: 'New Build',
  thp: 'Tenant Home Purchase',
  selfBuild: 'Self-build',
}

export function initRender(store, root, scheme) {
  const targets = Array.from(root.querySelectorAll('[data-derive], [data-show]'))
  const modeTargets = Array.from(root.querySelectorAll('[data-result-mode]'))
  const productTargets = Array.from(root.querySelectorAll('[data-product]'))
  const redemptionTypeTargets = Array.from(root.querySelectorAll('[data-redemption-type]'))
  const gateTargets = Array.from(root.querySelectorAll('[data-gate]'))
  const errorTargets = Array.from(root.querySelectorAll('[data-error]'))
  const rangeSlider = root.querySelector('#rangeAmount')

  function render(state) {
    const result = calculate({
      product: state.product,
      inputs: resolveInputs(state),
      redemption: state.redemption,
      scheme,
      early: state.isEarlyRedemption,
    })

    const lookup = withExtras(state, result, scheme)

    targets.forEach((el) => {
      const value = el.dataset.derive
        ? lookup[el.dataset.derive]
        : readPath(state, el.dataset.show)

      const formatType = el.dataset.format ?? 'plain'
      const formatter = FORMATTERS[formatType] ?? FORMATTERS.plain
      writeOutput(el, value, formatter)
    })

    toggleByMatch(modeTargets, 'resultMode', getResultMode(state))
    toggleByMatch(productTargets, 'product', state.product)
    toggleByMatch(redemptionTypeTargets, 'redemptionType', state.redemption?.type)
    applyGates(gateTargets, lookup)
    applyLocks(root, lookup)

    renderErrors(errorTargets, state, scheme)

    updateRangeSlider(rangeSlider, result)
    syncSlider(rangeSlider, store, state)
  }

  store.subscribe(render)
  //== armed steps live outside the calc store; re-render when they change
  //== so errors appear/disappear in response to Continue / Start Over
  subscribeArmed(() => render(store.getState()))
  render(store.getState())
}

//== show validation errors only for steps the user has tried to leave.
//== validate.js already has the per-step error map - we just gate display
//== on isArmed(currentStep).
function renderErrors(targets, state, scheme) {
  if (!targets.length) return
  const stepArmed = isArmed(state.currentStep)
  const validator = STEP_VALIDATORS[state.currentStep]
  const errors = stepArmed && validator ? (validator(state, scheme).errors ?? {}) : {}
  targets.forEach((el) => {
    const field = el.dataset.error
    const msg = errors[field]
    if (msg) {
      //== set hidden=false before content so aria-live="polite" picks up
      //== the text change while the element is in the accessibility tree
      el.hidden = false
      el.textContent = msg
    } else {
      el.hidden = true
    }
  })
}

//== which results-step layout to show. driven purely by the chosen redemption
//== type: early mode now keeps the redemption step, so the user makes a full /
//== partial choice there too. the early-window note is a separate element
//== gated on isEarlyMode, so it shows alongside either layout.
function getResultMode(state) {
  const type = state.redemption?.type
  if (type === 'partial' || type === 'full') return type
  return null
}

function writeOutput(el, value, formatter) {
  const isInput = el.tagName === 'INPUT'
  const isMissing =
    value == null ||
    value === '' ||
    (typeof value === 'number' && !Number.isFinite(value))
  if (isMissing) {
    if (isInput) el.value = ''
    else el.textContent = el.dataset.placeholder ?? PLACEHOLDER
    return
  }
  const formatted = formatter(value)
  if (isInput) el.value = formatted
  else el.textContent = formatted
}

//== keep slider bounds in sync with calc().minPartial / maxPartial.
//== browser auto-clamps slider.value when min/max change without firing
//== an input event, so the readonly text output is driven by data-show
//== rather than the existing range-slider.js handler for that case.
function updateRangeSlider(slider, result) {
  if (!slider) return
  if (Number.isFinite(result?.minPartial)) {
    slider.min = String(Math.ceil(result.minPartial))
  }
  if (Number.isFinite(result?.maxPartial)) {
    slider.max = String(Math.floor(result.maxPartial))
  }
}

//== mirror state into slider.value and reconcile if the browser auto-clamps to
//== the new min/max bounds. range-slider.js updates --fill on user input; we
//== update it here too so programmatic value changes stay visually in sync.
//== while redemption.amount is unset we park the thumb at the minimum without
//== writing to state, so the paired text input and the summary total stay
//== blank until the customer enters an amount (client 2026-07-21). a range
//== input always holds a value, so the thumb position is presentational only.
function syncSlider(slider, store, state) {
  if (!slider) return
  const amount = state.redemption?.amount

  if (amount == null) {
    slider.value = slider.min
    updateFill(slider)
    return
  }

  if (!Number.isFinite(amount)) return

  slider.value = String(amount)
  const clamped = Number(slider.value)
  if (clamped !== amount) {
    store.setState({ redemption: { amount: clamped } })
    return
  }
  updateFill(slider)
}

function updateFill(slider) {
  const min = Number(slider.min) || 0
  const max = Number(slider.max) || 0
  const value = Number(slider.value) || 0
  const range = max - min
  const fill = range > 0 ? ((value - min) / range) * 100 : 0
  slider.style.setProperty('--fill', `${fill}%`)
}

//== shared show/hide helper for data-result-mode / data-product /
//== data-redemption-type attributes. each target's attribute holds one or
//== more space-separated allowed values; the element is shown only when the
//== current state value matches one of them. used so the same pattern works
//== for product gating ("newBuild thp"), result mode ("early full"), and
//== redemption type ("partial" / "full").
function toggleByMatch(targets, datasetKey, currentValue) {
  targets.forEach((el) => {
    const allowed = (el.dataset[datasetKey] ?? '').split(/\s+/).filter(Boolean)
    el.hidden = !allowed.includes(currentValue)
  })
}

//== show or hide an element solely based on whether its [data-gate="<key>"]
//== derived value is ready (null / undefined / NaN / non-finite all count as
//== missing). do not also put data-product / data-result-mode / data-redemption-type
//== on the same element - they overwrite each other. to compose with one of
//== those, place data-gate on a child element whose parent carries the other
//== toggle; the parent hiding the subtree always wins visually.
function applyGates(targets, lookup) {
  targets.forEach((el) => {
    const value = lookup[el.dataset.gate]
    const missing =
      value == null ||
      (typeof value === 'number' && !Number.isFinite(value))
    el.hidden = missing
  })
}

//== [data-lock="<flagKey>"] inputs become read-only and display a derived
//== value while the named flag is set, and revert to an empty editable field
//== when it clears. used for the current equity share, which is fixed to the
//== original while a self-build is incomplete (client 2026-07-21).
//==
//== the value is written into the field but never into state - state holds only
//== what the customer entered, and the lock is applied at the point of use in
//== calculate.js resolveInputs. on unlock the field is cleared rather than left
//== holding the derived figure, so the customer can't be sitting in front of a
//== value the calculator doesn't have.
//==
//== unlike the other passes this queries per render rather than caching the
//== node list at init: the reset closures in inputs.js swap money / percent
//== inputs for parser-fresh elements on Start Over, so a cached reference would
//== point at a detached node afterwards.
//==
//== readonly rather than disabled - the field stays focusable and is announced
//== by screen readers, and the derived figure is information the customer may
//== want to read.
function applyLocks(root, lookup) {
  root.querySelectorAll('[data-lock]').forEach((el) => {
    const locked = lookup[el.dataset.lock] != null
    if (locked) {
      el.readOnly = true
      //== data-lock-value names the key to display while locked (the flag
      //== itself is just a boolean). omit it to lock without writing a value.
      const valueKey = el.dataset.lockValue
      if (valueKey) {
        const formatter = FORMATTERS[el.dataset.format ?? 'plain'] ?? FORMATTERS.plain
        writeOutput(el, lookup[valueKey], formatter)
      }
    } else if (el.readOnly) {
      //== only on the locked → unlocked transition, so we never clobber
      //== what the customer is currently typing into an editable field
      el.readOnly = false
      el.value = ''
    }
  })
}

//== merges calculate() output with cross-cutting derived values that
//== need both calc results and state (e.g. amountPayable, which depends
//== on isEarlyRedemption + redemption.type). HTML data-derive references
//== read from this combined lookup.
function withExtras(state, result, scheme) {
  const base = result ?? {}
  //== originalEquityPct only needs originalEquity plus purchasePrice
  //== (NB / THP) or buildCost (SB), but calculate() bails until the full
  //== input set is valid. fall back to a partial computation so the step
  //== 1 derived display (and the summary panel row) update as soon as
  //== those two inputs are entered.
  const originalEquityPct = base.originalEquityPct ?? originalEquitySharePct(state)
  const amountPayable = getAmountPayable(state, base)
  const currentEquityLocked = isCurrentEquityLocked(state)
  return {
    ...base,
    originalEquityPct,
    //== the effective current share, in percentage form - the entered value
    //== normally, the derived original share when locked. every display of the
    //== current share reads this (data-derive) rather than state.inputs
    //== directly, so the locked figure shows consistently on step 2, the
    //== result step, and the summary panel.
    currentEquityPct: resolveCurrentEquityPct(state),
    //== paired visibility flags for the current-share field's two help
    //== captions, and the read-only lock itself (data-lock). true (shown) /
    //== null (hidden), per the applyGates missing/ready rule.
    currentEquityLocked: currentEquityLocked ? true : null,
    currentEquityEditable: currentEquityLocked ? null : true,
    //== step-1 warning flag. true (shown) when the equity share exceeds the
    //== scheme cap, null (hidden) otherwise - matches the data-gate
    //== missing/ready rule in applyGates. drives the callout; the matching
    //== Continue hard block lives in validate.js validateStep1.
    originalEquityOverMax: computeOriginalEquityOverMax(originalEquityPct, scheme),
    //== step-1 warning flag. true (shown) when the current equity share
    //== exceeds the original, null (hidden) otherwise. drives the callout;
    //== the matching Continue hard block lives in validate.js validateStep1.
    currentEquityOverOriginal: computeCurrentEquityOverOriginal(state, originalEquityPct),
    //== summary-panel row value: the entered service charges, but only for a
    //== full redemption with a positive amount. null (hidden) otherwise, so a
    //== value retained from a prior full redemption doesn't linger after the
    //== user switches to partial. powers both the row's data-gate and its
    //== data-derive display.
    serviceChargesSummary: computeServiceChargesSummary(state),
    //== gate for the summary-panel Valuation row. hidden in early-redemption
    //== mode, where no new valuation is required (the valuation step is
    //== skipped) so the row would otherwise sit at "---" (client 2026-07).
    //== true (shown) / null (hidden), per the applyGates missing/ready rule.
    valuationApplicable: state.isEarlyRedemption ? null : true,
    //== gate for early-redemption-only messaging (step-0 callout, result-step
    //== "no new valuation required" note). true (shown) / null (hidden).
    isEarlyMode: state.isEarlyRedemption ? true : null,
    amountPayable,
    //== gate for the summary-panel total rows: the amount payable should not
    //== surface until the user has reached the Redemption stage (step 3) and a
    //== figure actually exists. keeps the early-redemption total from
    //== previewing before the redemption option is chosen (client 2026-07).
    //== true (shown) / null (hidden), per the applyGates missing/ready rule.
    amountPayableReady: state.currentStep >= 3 && amountPayable != null ? true : null,
    productLabel: PRODUCT_LABELS[state.product] ?? null,
    //== self-build only - sum of build cost + site value, used for the
    //== step 1 highlight row and the summary panel total
    totalPropertyValuation: computeTotalPropertyValuation(state),
    //== self-build only - same fallback story as originalEquityPct: only
    //== needs buildCost + siteValue, but calculate() bails until the full
    //== input set including currentValuation is present, so the step 1
    //== highlight row would otherwise stay at "-" until step 2 is filled
    siteValuePct: base.siteValuePct ?? computeSiteValuePct(state),
  }
}

function computeTotalPropertyValuation(state) {
  if (state.product !== PRODUCTS.SELF_BUILD) return null
  const inputs = state.inputs ?? {}
  const bc = inputs.buildCost
  const sv = inputs.siteValue
  if (!Number.isFinite(bc) || !Number.isFinite(sv)) return null
  return bc + sv
}

function computeSiteValuePct(state) {
  if (state.product !== PRODUCTS.SELF_BUILD) return null
  const inputs = state.inputs ?? {}
  const bc = inputs.buildCost
  const sv = inputs.siteValue
  if (!Number.isFinite(bc) || bc <= 0) return null
  if (!Number.isFinite(sv) || sv < 0) return null
  const total = bc + sv
  return total > 0 ? sv / total : null
}

//== returns true when the (decimal) equity share exceeds scheme.maxEquityPct,
//== else null so the warning callout stays hidden via data-gate. no cap
//== configured -> never warn.
function computeOriginalEquityOverMax(originalEquityPct, scheme) {
  const cap = scheme?.maxEquityPct
  if (!Number.isFinite(cap)) return null
  if (!Number.isFinite(originalEquityPct)) return null
  return originalEquityPct > cap ? true : null
}

//== returns true when the current equity share exceeds the original, else
//== null so the warning callout stays hidden via data-gate. currentEquityPct
//== is stored in percentage form (e.g. 5), originalEquityPct in decimal form
//== (e.g. 0.30), so divide the former by 100 before comparing.
function computeCurrentEquityOverOriginal(state, originalEquityPct) {
  //== unreachable while the share is locked to the original, so never warn
  if (isCurrentEquityLocked(state)) return null
  const current = state.inputs?.currentEquityPct
  if (!Number.isFinite(current)) return null
  if (!Number.isFinite(originalEquityPct)) return null
  return current / 100 > originalEquityPct ? true : null
}

//== returns the entered service charges when they should appear in the
//== summary panel (full redemption, amount > 0), else null so the row stays
//== hidden via data-gate.
function computeServiceChargesSummary(state) {
  if (state.redemption?.type !== 'full') return null
  const charges = state.inputs?.serviceCharges
  if (!Number.isFinite(charges) || charges <= 0) return null
  return charges
}

//== amount payable follows the chosen redemption type in all modes. the early
//== figure is no longer a shortcut to originalEquity - calculate()'s early
//== branch supplies fullRedemptionTotal (current share x base) so a prior
//== redemption is reflected. partial uses the entered amount.
function getAmountPayable(state, result) {
  const type = state.redemption?.type
  if (type === 'partial') return state.redemption?.amount ?? null
  if (type === 'full') return result?.fullRedemptionTotal ?? null
  return null
}

function readPath(obj, path) {
  if (!path) return undefined
  return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj)
}
